import { createExternalInvoices } from 'api/Billing';
import { updateInvoices } from 'api/Invoice';
import { kioskCommand } from 'api/Kiosk';

import { ia, iaRa, mString } from 'lib/helpers/utility';

import { showAlert } from 'components/shared/Alert/Alert';

import { chargeElavonOnlineHandler, elavonSavedCardHandler } from './elavonMethods';
import {
  appliedBalanceHandler,
  chargeCashHandler,
  chargeCheckHandler,
  chargeOutsideFinancingHandler,
  noPaymentSelectedHandler
} from './genericMethods';
import { chargePayrocPaymentHandler, saveCardHandler } from './payrocMethods';

const sendCommandToKiosk = async (
  navigate,
  {
    methodDetail,
    amount,
    invoices,
    appliedBalance,
    surchargeAmount,
    patientId,
    data,
    terminalId,
    saveCard,
    ..._rest
  }
) => {
  if (!methodDetail) {
    return;
  }

  await updateInvoices(navigate, { invoices });
  const internalDetails = iaRa(invoices).map(({ id, balance }) => ({
    id,
    amount_paid: balance
  }));

  const selectedInternalInvoices = [
    {
      amount_cents: Math.round(amount) - (surchargeAmount || 0),
      total_amount_cents: Math.round(amount),
      due_date: new Date(),
      internal_invoice_ids: iaRa(invoices).map((i) => i.id),
      memo: '',
      patient: { id: patientId },
      tax_percentage: 0,
      surcharge_amount: surchargeAmount,
      aeob: false,
      is_detailed: false,
      type: 'kiosk',
      internal_details: internalDetails
    }
  ];

  const newExternalInvoice = await createExternalInvoices(navigate, {
    invoices: selectedInternalInvoices,
    skipConfirmation: true
  });

  if (!ia(newExternalInvoice?.invoices)) {
    showAlert({
      color: 'danger',
      title: 'Unable to send command to the kiosk.',
      message: 'Please try adding an invoice.'
    });
    return;
  }

  const extInvoice = newExternalInvoice?.invoices?.[0];

  if (!extInvoice?.link) {
    showAlert({
      color: 'danger',
      title: 'Unable to send command to the kiosk.',
      message:
        'No invoices selected to make a payments or invoices selected ineligible for payment. Please select a different invoice.'
    });
    return;
  }

  const kioskCommandRes = await kioskCommand(navigate, {
    kioskId: methodDetail,
    command: 'url-change',
    url: extInvoice?.link,
    patientId: extInvoice?.patient_id,
    externalInvoiceToken: extInvoice?.token,
    page: 'Payment',
    data: { ...data, externalInvoiceToken: extInvoice?.token, terminalId, saveCard },
    appliedBalance
  });

  if (kioskCommandRes?.code !== 0) {
    throw new Error(kioskCommandRes);
  }

  showAlert({
    icon: 'outbound-fax-success',
    color: 'success',
    title: `Payment Request Sent`,
    message: `Payment request sent to kiosk ${kioskCommandRes?.kioskName} for ${mString(amount)}`,
    duration: 15000,
    footerActions: {
      primary: {
        text: 'Cancel Payment',
        color: 'danger',
        onClick: async () => {
          await kioskCommand(navigate, {
            kioskId: methodDetail,
            command: 'payment-cancel'
          });
          await kioskCommand(navigate, {
            kioskId: methodDetail,
            command: 'idle'
          });
          showAlert({
            icon: 'outbound-fax-error',
            color: 'success',
            title: `Payment Request Cancelled`,
            message: `Payment request cancelled.`
          });
        }
      }
    }
  });
};

export const paymentMethodApiElavon = {
  card: (navigate, props) =>
    sendCommandToKiosk(navigate, { type: 'card', data: { processor: 'elavon' }, ...props }),
  'saved-cards': elavonSavedCardHandler,
  'credit-card': chargeElavonOnlineHandler,
  'pre-auth': (navigate, props) =>
    chargeElavonOnlineHandler(navigate, { tokenOnly: true, ...props })
};

export const paymentMethodApiPayroc = {
  card: (navigate, props) =>
    sendCommandToKiosk(navigate, { type: 'card', data: { processor: 'payroc' }, ...props }),
  'saved-cards': (navigate, props) =>
    chargePayrocPaymentHandler(navigate, { type: 'card', ...props }),
  ach: (navigate, props) => chargePayrocPaymentHandler(navigate, { type: 'ach', ...props }),
  'credit-card': (navigate, props) =>
    chargePayrocPaymentHandler(navigate, { type: 'card', ...props }),
  'pre-auth': (navigate, props) => saveCardHandler(navigate, { type: 'card', ...props }),
  vpayment: (navigate, props) => {
    return chargePayrocPaymentHandler(navigate, { type: 'card', ...props });
  }
};

export const genericMethodsApi = {
  cash: chargeCashHandler,
  check: chargeCheckHandler,
  'outside-financing': chargeOutsideFinancingHandler,
  'skip-payment': noPaymentSelectedHandler,
  'recurring-request': () => {},
  'applied-balance': appliedBalanceHandler
};
